<template>
  <div>
    <transition name="fade">
      <GuideVideoBanner
        v-if="guideData && showGuideCard"
        :text="guideData.content.text"
        :subtext="guideData.content.subText"
        :videoUrl="guideData.content.videoUrl"
        :videoThumbnail="guideData.content.videoThumbnail"
        @close="handleGuideClose"
      />
    </transition>
    <EmployeesWithNoAccessAlertBanner
      :activeEmployeesWithNoaccess="activeEmployeesWithNoaccess"
      :description="description"
      :actionLabel="actionLabel"
      @isFilter="handleFilterClick"
    />
    <div class="org-employee-tab">
      <EmployeesOrganizationTable
        :employees="employees"
        ref="employeesTableRef"
        @drilldown="handleEmployeeDrilldown"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import EmployeesOrganizationTable from '@components/EmployeesOrganizationTable/EmployeesOrganizationTable.vue';
import EmployeesWithNoAccessAlertBanner from "@components/EmployeesWithNoAccessAlertBanner/EmployeesWithNoAccessAlertBanner.vue";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import Knock, { KnockGuideClient } from "@knocklabs/client";
import configService from "../../account/src/services/config";

const emit = defineEmits(['drilldown']);

const props = defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
  activeEmployeesWithNoaccess: {
    type: Number,
    default: 0
  },
  description: {
    type: String,
    default: ""
  },
  actionLabel: {
    type: String,
    default: "Apply Filter"
  },
  mockGuideData: {
    type: Object,
    default: null,
  },
});

const viewState = useEmployeesOrganizationTableViewState();

const guideData = ref(null);
const showGuideCard = ref(true);
const knockClient = ref(null);
const guideClient = ref(null);
const guides = ref([]);

const initializeKnock = async () => {
  try {
    const authUser = JSON.parse(localStorage.getItem("user"));
    const apiKey = configService.get("knockApiKey");
    const userId = authUser?.id?.toString();
    const channelId = configService.get("knockGuideChannelId");

    knockClient.value = new Knock(apiKey);
    knockClient.value.authenticate({ id: userId });

    guideClient.value = new KnockGuideClient(knockClient.value, channelId);

    guideClient.value.store.subscribe(() => {
      const storeState = guideClient.value.store.state;
      guides.value = guideClient.value.selectGuides(storeState) || [];
      const employeesGuide = guides.value.find(
        (guide) => guide.key === "employees-and-position-assignments"
      );
      guideData.value = employeesGuide?.steps?.[0] || null;
    });

    await guideClient.value.subscribe();
    await guideClient.value.fetch();
  } catch (error) {
    console.error("Error initializing Knock:", error);
  }
};

const handleGuideClose = async () => {
  showGuideCard.value = false;
  
  if (guideData.value && guideData.value.markAsArchived) {
    try {
      await guideData.value.markAsArchived();
    } catch (error) {
      console.error('Error archiving guide in Knock:', error);
    }
  }
};

onMounted(async () => {
  if (props.mockGuideData) {
    guideData.value = props.mockGuideData;
  } else {
    await initializeKnock();
  }
});

const handleFilterClick = () => {
  const noAccessFilter = { label: "No Access", value: "no_access" };
  const activeStatusFilter = { label: "Active", value: "active" };
  const currentFilters = viewState.state.filters.values || [];
  
  const hasNoAccessFilter = currentFilters.some(
    (f) => (typeof f === 'object' ? f.value : f) === "no_access"
  );
  
  if (!hasNoAccessFilter) {
    // When applying filter from banner, replace all existing filters (including defaults)
    // with only "no_access" and "active" filters to show only active employees without access
    // This removes default filters like "draft" that might be present
    const newFilters = [noAccessFilter, activeStatusFilter];
    
    viewState.applyFilters(newFilters);
  } else {
    viewState.state.filters.showFilterDialog = true;
  }
};

const handleEmployeeDrilldown = (data) => {
  console.log('🟣 OrganizationEmployeesTab.handleEmployeeDrilldown() called', data);
  emit('drilldown', data);
  console.log('🟣 OrganizationEmployeesTab drilldown event emitted');
};
</script>

<style scoped>
.org-employee-tab {
  margin-top: 15px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

::v-deep .guide-video-banner {
  margin-bottom: 15px;
}
</style>